<script setup lang="ts" generic="T">
  import { type CellRenderProps, VTooltip } from "@krainovsd/vue-ui";
  import dayjs from "dayjs";
  import { computed } from "vue";

  type Props = {
    // Row field holding the grouping value (name or date).
    field: string;
    // Date fields are formatted with `dateFormat`.
    isDate?: boolean;
    dateFormat?: string;
    // Row field holding the entity id. When it resolves to a positive number the
    // cell is prefixed with it, e.g. "123 - Trigger name". Date rows carry no id,
    // so they show the bare value.
    idField?: string;
    // Wrap onto up to three lines (with an ellipsis past that) instead of a
    // single truncated line.
    multiline?: boolean;
  };

  const props = defineProps<CellRenderProps<T, Props>>();

  // A real entity id is a positive number; `0`/undefined (date or placeholder
  // rows) means "no id", so the value is shown without a prefix.
  function hasEntityId(value: unknown): value is number {
    return typeof value === "number" && value > 0;
  }

  const display = computed(() => {
    const original = props.context.row.original as Record<string, unknown>;
    const value = original[props.settings?.field ?? ""];

    let text = "";
    if (props.settings?.isDate && typeof value === "string") {
      const parsed = dayjs(value);
      text = parsed.isValid() ? parsed.format(props.settings.dateFormat ?? "DD.MM.YYYY") : value;
    } else if (typeof value === "string" || typeof value === "number") {
      text = String(value);
    }

    const idField = props.settings?.idField;
    const id = idField ? original[idField] : undefined;
    return text && hasEntityId(id) ? `${id} - ${text}` : text;
  });
</script>

<template>
  <div :class="$style.base">
    <!-- Full value on hover, shown only when the cell is actually truncated. -->
    <VTooltip :class="$style.tooltip" :text="display" :open-not-visible="true">
      <span :class="[$style.text, $props.settings?.multiline && $style.multiline]">{{
        display
      }}</span>
    </VTooltip>
  </div>
</template>

<style lang="scss" module>
  .base {
    display: flex;
    align-items: center;

    width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  .tooltip {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  .text {
    min-width: 0;
    max-width: 100%;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Wrap onto at most three lines, clipping with an ellipsis beyond that.
  .multiline {
    white-space: normal;

    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-clamp: 3;
  }
</style>
